import React from 'react';
import { resolveEstimateDescriptionInline } from '../../utils/estimate-description';

interface RomanShadeJson {
    style: {
        function: number;
        type: string;
        windowMount: number;
        boardMount: number;
    };
    dimensions: {
        finishedWidth: number | null;
        finishedLength: number | null;
        foldSize: number | null | undefined;
        numOfPermanentFolds: number | null;
        bottomSkirit: number;
    };
    headrail: {
        boardDepth: number | null;
    };
    valanceAndReturns: {
        backValance: number;
        frontValance: number;
        sides: string | null;
    };
}

interface RomanShadeSummaryProps {
    salesDescriptionSettingsMapped: { id: string; isEnabled: boolean }[];
    romanShadeJson: RomanShadeJson | null;
    liningsList: {
        name: string;
        active: boolean;
        draperyOrderLiningsFabric?: { pattern?: string, orderOrigin?: number, yardsNeeded?: any };
    }[];
    liningsPatternItems?: any[];
    description: string | undefined;
}

export const RomanShadeDescription: React.FC<RomanShadeSummaryProps> = ({
    salesDescriptionSettingsMapped,
    romanShadeJson,
    liningsList,
    liningsPatternItems = [],
    description,
}) => {
    if (!romanShadeJson) {
        return null;
    }

    const draperyOrderDesignFunction: any = {
        0: "Functional",
        1: "Faux",
    };
    const draperyOrderDesignStyleWindowMount: any = {
        0: "Inside",
        1: "Outside",
    };
    const draperyOrderDesignStyleBoardMount: any = {
        0: "Waterfall",
        1: "Reverse",
    }
    const draperyOrderDesignValanceAndReturnsSides: any = {
        "side-flaps": "Sides Flaps",
        "returns": "Wrapped Returns",
    }

    const isSettingEnabled = (id: string) =>
        salesDescriptionSettingsMapped.some(item => item.id === id && item.isEnabled);

    const calculateSqFt = (): string => {
        const { dimensions, style, headrail, valanceAndReturns } = romanShadeJson;
        if (!dimensions.finishedWidth && !dimensions.finishedLength && !dimensions.foldSize && !dimensions.numOfPermanentFolds) return "";
        const width = Number(dimensions.finishedWidth) || 0;
        const length = Number(dimensions.finishedLength) || 0;
        const boardDepth = Number(headrail.boardDepth) || 0;
        const returnsAddition = valanceAndReturns.sides === "returns" && valanceAndReturns.frontValance === 0 ? 2 * boardDepth : 0;
        const foldSize = Number(dimensions.foldSize) || 0;
        const numFolds = Number(dimensions.numOfPermanentFolds) || 0;
        const foldsAddition = foldSize && numFolds ? foldSize * 2 * numFolds : 0;
        const totalArea = (width + returnsAddition) * (length + foldsAddition);
        const sqFt = Math.ceil(totalArea / 144);
        if (sqFt === 0) return "";
        const prefix = style.function !== -1 || style.type ? " / " : "";
        return `${prefix}${sqFt} Sq. Ft.`;
    };

    const formatDimensions = (): string => {
        const { dimensions } = romanShadeJson;

        if (dimensions.finishedWidth === null || dimensions.finishedLength === null) {
            return "";
        }

        const numFolds = Number(dimensions.numOfPermanentFolds);
        const foldSize = Number(dimensions.foldSize);
        const hasFolds = numFolds > 0 && foldSize > 0;

        let dimensionString = `${dimensions.finishedWidth}''W x `;

        if (hasFolds) {
            const foldsLength = numFolds * foldSize * 2;
            dimensionString += `(${dimensions.finishedLength}''L + ${foldsLength}''L for Permanent Folds)`;
        } else {
            dimensionString += `${dimensions.finishedLength}''L`;
        }

        return dimensionString;
    };

    const isLiningNoEnabled = isSettingEnabled("saleslining:no");
    const isLiningYesEnabled = isSettingEnabled("saleslining:yes");
    const isShowLiningNameEnabled = isSettingEnabled("salesshowliningname");
    const isLiningPurchaserIfComEnabled = isSettingEnabled("salesliningpurchaserifcom");
    const isLiningYardsIfComEnabled = isSettingEnabled("salesliningyardsifcom");
    const isLiningYardsIfWorkroomEnabled = isSettingEnabled("salesliningyardsifworkroompurchasing");
    const isInterliningNoEnabled = isSettingEnabled("salesinterlining:no");
    const isInterliningYesEnabled = isSettingEnabled("salesinterlining:yes");
    const isShowInterliningNameEnabled = isSettingEnabled("salesshowinterliningname");
    const isInterliningPurchaserIfComEnabled = isSettingEnabled("salesinterliningpurchaserifcom");
    const isInterliningYardsIfComEnabled = isSettingEnabled("salesinterliningyardsifcom");
    const isInterliningYardsIfWorkroomEnabled = isSettingEnabled("salesinterliningyardsifworkroompurchasing");

    const liningItem = liningsList.find((l: any) => l.name === "Lining" && l.active)
    const liningOrderOrigin = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.orderOrigin
    const liningItemName = resolveEstimateDescriptionInline({
        fabric: liningItem && liningItem.draperyOrderLiningsFabric,
        items: liningsPatternItems,
        fallbackMode: "pattern-only",
    })
    const liningYardsNeeded = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.yardsNeeded
    const interliningItem = liningsList.find((l: any) => l.name === "Interlining" && l.active)
    const interliningOrderOrigin = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.orderOrigin
    const interliningItemName = resolveEstimateDescriptionInline({
        fabric: interliningItem && interliningItem.draperyOrderLiningsFabric,
        items: liningsPatternItems,
        fallbackMode: "pattern-only",
    })
    const interliningYardsNeeded = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.yardsNeeded
    
    const hasLiningItem = Boolean(liningItem);
    const hasInterliningItem = Boolean(interliningItem);
    const shouldShowLiningSection =
    isLiningNoEnabled ||
    isLiningYesEnabled ||
    isShowLiningNameEnabled ||
    isLiningPurchaserIfComEnabled ||
    isLiningYardsIfComEnabled ||
    isLiningYardsIfWorkroomEnabled;

    const shouldShowInterliningSection =
        isInterliningNoEnabled ||
        isInterliningYesEnabled ||
        isShowInterliningNameEnabled ||
        isInterliningPurchaserIfComEnabled ||
        isInterliningYardsIfComEnabled ||
        isInterliningYardsIfWorkroomEnabled;

    const shouldShowLiningYes = hasLiningItem && isLiningYesEnabled;
    const shouldShowLiningName = hasLiningItem && isShowLiningNameEnabled && Boolean(liningItemName);
    const shouldShowLiningCom = hasLiningItem && liningOrderOrigin === 2 && isLiningPurchaserIfComEnabled;
    const shouldShowLiningYardsFromCom = hasLiningItem && liningOrderOrigin === 2 && isLiningYardsIfComEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYardsFromWorkroom = hasLiningItem && liningOrderOrigin === 1 && isLiningYardsIfWorkroomEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYards = shouldShowLiningYardsFromCom || shouldShowLiningYardsFromWorkroom;
    const liningDetailParts: string[] = [];
    if (hasLiningItem) {
        if (shouldShowLiningYes) {
            liningDetailParts.push("Yes");
        }
        if (shouldShowLiningName && liningItemName) {
            liningDetailParts.push(liningItemName);
        }
        if (shouldShowLiningYards) {
            liningDetailParts.push(`${liningYardsNeeded} yards`);
        }
    } else if (isLiningNoEnabled) {
        liningDetailParts.push("No");
    }
    const shouldRenderLining =
        shouldShowLiningSection &&
        ((hasLiningItem && (liningDetailParts.length > 0 || shouldShowLiningCom)) ||
            (!hasLiningItem && liningDetailParts.length > 0));

    const shouldShowInterliningYes = hasInterliningItem && isInterliningYesEnabled;
    const shouldShowInterliningName = hasInterliningItem && isShowInterliningNameEnabled && Boolean(interliningItemName);
    const shouldShowInterliningCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningPurchaserIfComEnabled;
    const shouldShowInterliningYardsFromCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningYardsIfComEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYardsFromWorkroom = hasInterliningItem && interliningOrderOrigin === 1 && isInterliningYardsIfWorkroomEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYards = shouldShowInterliningYardsFromCom || shouldShowInterliningYardsFromWorkroom;
    const interliningDetailParts: string[] = [];
    if (hasInterliningItem) {
        if (shouldShowInterliningYes) {
            interliningDetailParts.push("Yes");
        }
        if (shouldShowInterliningName && interliningItemName) {
            interliningDetailParts.push(interliningItemName);
        }
        if (shouldShowInterliningYards) {
            interliningDetailParts.push(`${interliningYardsNeeded} yards`);
        }
    } else if (isInterliningNoEnabled) {
        interliningDetailParts.push("No");
    }
    const shouldRenderInterlining =
        shouldShowInterliningSection &&
        ((hasInterliningItem && (interliningDetailParts.length > 0 || shouldShowInterliningCom)) ||
            (!hasInterliningItem && interliningDetailParts.length > 0));

    return (
        <div>
            <div>
                <span className="nonBold">
                    {romanShadeJson.style.function !== -1 ? draperyOrderDesignFunction[romanShadeJson.style.function] : ""}
                    {romanShadeJson.style.type ? (romanShadeJson.style.function !== -1 ? " / " : "") + romanShadeJson.style.type.charAt(0).toUpperCase() + romanShadeJson.style.type.substring(1) : ""}
                    {calculateSqFt()}
                </span>
                <br />
                <span className="nonBold">{description || ''}</span>
            </div>
            <div>
                <span className="nonBold">
                    {formatDimensions()}
                </span>
            </div>

            <div>
                <span className="nonBold">
                    {romanShadeJson.style.windowMount !== -1 ? `${draperyOrderDesignStyleWindowMount[romanShadeJson.style.windowMount]} Mount` : ""}
                    {romanShadeJson.style.boardMount !== -1 ? `${romanShadeJson.style.windowMount !== -1 ? ' / ' : ''}${draperyOrderDesignStyleBoardMount[romanShadeJson.style.boardMount]}` : ""}
                    {romanShadeJson.valanceAndReturns.backValance === 1 ? `${romanShadeJson.style.boardMount !== -1 || romanShadeJson.style.windowMount !== -1 ? ' / ' : ''}Back Valance` : ""}
                </span>
            </div>
            <div>
                <span className="nonBold">
                    {romanShadeJson.valanceAndReturns.frontValance === 1 ? "Front Valance" : ""}
                    {romanShadeJson.valanceAndReturns.sides && romanShadeJson.valanceAndReturns.sides !== "none" ? `${romanShadeJson.valanceAndReturns.frontValance === 1 ? ' / ' : ''}${draperyOrderDesignValanceAndReturnsSides[romanShadeJson.valanceAndReturns.sides]}` : ""}
                    {romanShadeJson.dimensions.bottomSkirit === 1 ? `${(romanShadeJson.valanceAndReturns.sides && romanShadeJson.valanceAndReturns.sides !== "none") || romanShadeJson.valanceAndReturns.frontValance === 1 ? ' / ' : ''}Bottom Skirt` : ""}
                </span>
            </div>
            <>
                {shouldRenderLining && (
                    <>
                        {shouldShowLiningCom &&
                            <span>
                                {`COM `}
                            </span>
                        }
                        <span>
                            Lining:
                        </span>
                        {liningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {liningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
                {shouldRenderInterlining && (
                    <>
                        {shouldShowInterliningCom &&
                            <span>
                                {`COM `}
                            </span>
                        }
                        <span>
                            Interlining:
                        </span>
                        {interliningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {interliningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
            </>
        </div>
    );
};
